import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Info, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';

type Feasibility = 'Fully Feasible' | 'Partially Feasible' | 'Not Feasible';

const TechReferenceGuide = () => {
  const [activeTab, setActiveTab] = useState('use-cases');

  const useCases = [
    {
      scenario: '1. Unauthorized Free Accounts',
      feasibility: 'Fully Feasible',
      comments: 'AI can detect anomalies in account status by comparing against expected billing profiles.',
      technologies: ['Anomaly Detection Algorithms', 'Isolation Forests', 'Autoencoders']
    },
    {
      scenario: '2. Promotion and Discount Errors',
      feasibility: 'Fully Feasible',
      comments: 'AI models can monitor promotion timelines, detect incorrectly applied discounts, and identify overlapping promotions.',
      technologies: ['Time-Series Analysis', 'Rule-Based Systems', 'Clustering Algorithms']
    },
    {
      scenario: '3. Excessive Credits from Customer Service Agents',
      feasibility: 'Fully Feasible',
      comments: 'Statistical models can identify outliers in credit amounts and frequencies granted by agents.',
      technologies: ['Statistical Outlier Detection', 'Clustering Algorithms', 'Anomaly Detection']
    },
    {
      scenario: '4. Prorate and Billing Cycle Errors',
      feasibility: 'Fully Feasible',
      comments: 'AI can verify prorated charges, ensure billing cycles are correctly applied, and detect delayed billing issues.',
      technologies: ['Rule-Based Systems','Time-Series Analysis','Process Mining']
    },
    {
      scenario: '5. Tax and Regulatory Compliance Issues',
      feasibility: 'Fully Feasible',
      comments: 'Tax calculation engines and compliance checkers can validate taxes and ensure adherence to regulations.',
      technologies: ['Rule-Based Systems','Knowledge Graphs','Compliance Checking Algorithms']
    },
    {
      scenario: '6. Mis-charges and Inaccurate Usage Data',
      feasibility: 'Fully Feasible',
      comments: 'AI can compare billed amounts with expected charges based on correct usage data and detect anomalies in usage records.',
      technologies: ['Anomaly Detection Algorithms','Predictive Modeling','Autoencoders']
    },
    {
      scenario: '7. Impacts from Unsuccessful Planned Changes and Data Integration Errors',
      feasibility: 'Partially Feasible',
      comments: 'Monitoring system integrations and deployment logs can detect discrepancies; may require robust integration between systems.',
      technologies: ['Log Analysis using NLP','Time-Series Change Detection','Anomaly Detection']
    },
    {
      scenario: '8. Order Entry and Provisioning Errors by Customer Service Agents',
      feasibility: 'Fully Feasible',
      comments: 'Machine learning can detect anomalies in order entries and provisioning by customer service agents.',
      technologies: ['Anomaly Detection','Clustering Algorithms','Process Mining']
    },
    {
      scenario: '9. Order Entry Errors by Online Self-Service Platforms',
      feasibility: 'Fully Feasible',
      comments: 'AI can monitor and validate online orders for anomalies or inconsistencies.',
      technologies: ['Anomaly Detection','User Behavior Analytics','Autoencoders']
    },
    {
      scenario: '10. Fraudulent Activities (e.g., Identity Theft, Account Qualification Fraud)',
      feasibility: 'Fully Feasible',
      comments: 'AI can detect unusual patterns indicative of fraud through anomaly detection techniques.',
      technologies: ['Anomaly Detection','Clustering Algorithms','Graph Analysis']
    },
    {
      scenario: '11. Unauthorized Plan Changes',
      feasibility: 'Fully Feasible',
      comments: 'AI can monitor plan changes and flag unauthorized modifications.',
      technologies: ['Anomaly Detection','Rule-Based Systems','Process Mining']
    },
    {
      scenario: '12. Duplicate Charges',
      feasibility: 'Fully Feasible',
      comments: 'Systems can detect multiple charges for the same service within billing cycles.',
      technologies: ['Duplicate Detection Algorithms','Rule-Based Systems']
    },
    {
      scenario: '13. Account Hierarchy Billing Problems for Enterprise Customers',
      feasibility: 'Fully Feasible',
      comments: 'AI can analyze complex account hierarchies and identify billing inconsistencies for enterprise clients.',
      technologies: ['Graph Analysis','Anomaly Detection','Clustering Algorithms']
    },
    {
      scenario: '14. Account Transfer Errors',
      feasibility: 'Fully Feasible',
      comments: 'Systems can validate account transfers to ensure accurate billing during ownership changes.',
      technologies: ['Process Mining','Anomaly Detection','Rule-Based Systems']
    },
    {
      scenario: '15. Expired Payment Methods',
      feasibility: 'Fully Feasible',
      comments: 'Systems can detect expired payment methods and prompt updates to prevent billing failures.',
      technologies: ['Rule-Based Systems','Predictive Modeling','Time-Series Analysis']
    },
    {
      scenario: '16. MDU and Bulk Billing Account Errors',
      feasibility: 'Fully Feasible',
      comments: 'AI can manage bulk accounts (Multi-Dwelling Units) and detect billing anomalies in bulk billing arrangements.',
      technologies: ['Anomaly Detection','Graph Analysis','Clustering Algorithms']
    },
    {
      scenario: '17. Missing Charges',
      feasibility: 'Fully Feasible',
      comments: 'AI can identify patterns of missing charges but may require integration with usage data.',
      technologies: ['Anomaly Detection', 'Time-Series Analysis', 'Statistical Outlier Detection']
    },
    {
      scenario: '18. Incorrect Rate Application',
      feasibility: 'Fully Feasible',
      comments: 'ML can verify correct rate application across customer segments and usage patterns.',
      technologies: ['Rule-Based Systems', 'Clustering Algorithms', 'Anomaly Detection']
    },
    {
      scenario: '19. Contract Compliance Issues',
      feasibility: 'Fully Feasible',
      comments: 'AI can monitor contract terms and identify violations or deviations.',
      technologies: ['Compliance Checking Algorithms', 'Rule-Based Systems']
    },
    {
      scenario: '20. System Integration Errors',
      feasibility: 'Fully Feasible',
      comments: 'ML can detect anomalies in data flow and system interactions.',
      technologies: ['Log Analysis using NLP', 'Anomaly Detection', 'Process Mining']
    },
    {
      scenario: '21. Manual Override Errors',
      feasibility: 'Fully Feasible',
      comments: 'ML can detect patterns of incorrect manual interventions.',
      technologies: ['User Behavior Analytics', 'Anomaly Detection', 'Process Mining']
    },
    {
      scenario: '22. Service Level Mismatches',
      feasibility: 'Fully Feasible',
      comments: 'ML can detect discrepancies between service levels and billing.',
      technologies: ['Compliance Checking Algorithms', 'Rule-Based Systems', 'Process Mining']
    },
    {
      scenario: '23. Incomplete Account Setup',
      feasibility: 'Fully Feasible',
      comments: 'AI can verify completeness of account setup and required fields.',
      technologies: ['Rule-Based Systems', 'Process Mining', 'Compliance Checking Algorithms']
    }
  ];

  const classifications = [
    {
      term: 'AI (Artificial Intelligence)',
      definition: 'Technologies that mimic human intelligence and decision-making capabilities. This includes performing tasks that typically require human intelligence, such as learning and problem-solving, understanding information, reasoning, and learning from experience. - Mistral Large',
      businessNote: 'While powerful, AI requires careful implementation and has specific limitations. It\'s not a monolithic universal solution and it works best when the right people select the right tools and techniques for addressing well-defined problems with quality data.',
      examples: 'Pattern recognition, automated decision-making systems'
    },
    {
      term: 'ML (Machine Learning)',
      definition: 'Machine Learning (ML) is a subset of artificial intelligence that focuses on developing algorithms and statistical models enabling computers to learn from and make decisions based on data. ML techniques allow systems to improve their performance on a task over time without being explicitly programmed for every scenario. It includes both supervised and unsupervised learning methods. - GPT o1',
      businessNote: 'Requires quality data and clear objectives. Most effective for specific, data-driven tasks rather than general problem-solving. Model maintenance, performance monitoring, and data drift detection are essential for maintaining accuracy and effectiveness over time.',
      examples: 'Anomaly detection, predictive modeling, clustering'
    },
    {
      term: 'Legacy/Traditional',
      definition: 'Established technologies that are widely used but may not represent the latest innovations. - GPT 4o',
      businessNote: 'Often more reliable and well-understood than cutting-edge solutions. May be the best choice for certain business needs, especially where stability and compliance are paramount. However, consider potential limitations in scalability, performance, and integration with modern systems. Modernization strategies should be evaluated over time.',
      examples: 'Rule-based systems, statistical analysis'
    },
    {
      term: 'Advanced Technology',
      definition: 'Technologies that are sophisticated and often involve complex algorithms or computational techniques but may not necessarily fall under AI or ML.  - Mistral Large',
      businessNote: 'These technologies often provide significant competitive advantages by enabling complex analysis and optimization. However, they may require specialized expertise and infrastructure. Careful evaluation of cost-benefit and integration with existing systems is crucial.',
      examples: 'Geospatial analysis, compliance checking algorithms'
    }, 
    {
      term: 'Emerging Technology',
      definition: 'Relatively new technologies, or rapidly evolving applications of existing technologies, with significant potential for future impact. - Gemini Pro 1.5',
      businessNote: 'Emerging technologies offer opportunities for disruptive innovation and competitive differentiation. However, they also carry higher risks due to their rapidly evolving nature and lack of established best practices. Early adoption can provide first-mover advantages, but requires careful monitoring, experimentation, and a willingness to adapt. A clear understanding of the technology\'s potential applications and limitations is crucial, as is establishing a framework for risk mitigation. Proof of concept projects and strategic partnerships can be valuable for exploring these technologies.',
      examples: 'Knowledge graphs, process mining, generative AI'
    }    
  ];

  const technologies = [
    {
      name: 'Anomaly Detection',
      description: 'Identifies unusual patterns or behaviors that deviate from expected norms in datasets.',
      businessValue: 'Automatically detects suspicious activities, potential system failures, or security threats before they become major issues.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Requires historical data to establish "normal" patterns. Most effective when customized to specific use cases.'
    },
    {
      name: 'Anomaly Detection Algorithms',
      description: 'Specific mathematical and statistical methods for finding unusual patterns in data.',
      businessValue: 'Provides concrete tools for implementing anomaly detection in specific business contexts, such as fraud detection or quality control.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Different algorithms suit different types of data and use cases. Requires expertise to select and tune appropriate methods.'
    },
    {
      name: 'Autoencoders',
      description: 'Neural networks that learn to compress and reconstruct data efficiently.',
      businessValue: 'Useful for detecting anomalies, reducing data complexity, and finding patterns in large datasets.',
      classification: 'ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Requires significant technical expertise and training data. Best suited for specialized applications.'
    },
    {
      name: 'Clustering Algorithms',
      description: 'Techniques for automatically grouping similar data points together.',
      businessValue: 'Helps discover natural patterns in data, useful for customer segmentation and pattern discovery.',
      classification: 'ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Results can be subjective and require domain expertise to interpret. Different algorithms suit different data types.'
    },
    {
      name: 'Compliance Checking Algorithms',
      description: 'Automated methods for verifying if processes or data conform to defined rules and regulations.',
      businessValue: 'Reduces risk of regulatory violations and ensures consistent policy enforcement.',
      classification: 'ML',
      source: 'GPT-4o',
      implementation: 'Requires clear definition of compliance rules and regular updates to maintain accuracy.'
    },
    {
      name: 'Duplicate Detection Algorithms',
      description: 'Methods for identifying repeated or highly similar items in datasets.',
      businessValue: 'Improves data quality and reduces waste from duplicate records or transactions.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Needs careful tuning to balance between false positives and missed duplicates.'
    },
    {
      name: 'Graph Analysis',
      description: 'Techniques for analyzing relationships and connections within networks of data.',
      businessValue: 'Reveals hidden patterns in connected data, useful for fraud detection and relationship mapping.',
      classification: 'ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Requires specialized data structures and visualization tools. Can be computationally intensive.'
    },
    {
      name: 'Isolation Forests',
      description: 'A specific machine learning technique for detecting anomalies based on data isolation.',
      businessValue: 'Efficiently identifies unusual cases in large datasets with minimal configuration.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Works well with high-dimensional data but requires understanding of parameter tuning.'
    },
    {
      name: 'Knowledge Graphs',
      description: 'Structured representations of information showing relationships between entities.',
      businessValue: 'Improves information retrieval and enables complex relationship analysis.',
      classification: 'AI/ML',
      source: 'GPT-4o',
      implementation: 'Requires ongoing maintenance and curation. Benefits increase with scale of information.'
    },
    {
      name: 'Log Analysis using NLP',
      description: 'Application of natural language processing to analyze system and application logs.',
      businessValue: 'Automates the discovery of issues and patterns in system logs and technical documentation.',
      classification: 'AI/ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Needs training on specific log formats and domain terminology.'
    },
    {
      name: 'Predictive Modeling',
      description: 'Creating statistical models to forecast future outcomes based on historical data.',
      businessValue: 'Enables data-driven decision making and proactive problem addressing.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Requires quality historical data and regular model validation and updates.'
    },
    {
      name: 'Process Mining',
      description: 'Techniques for discovering and analyzing business processes from event logs.',
      businessValue: 'Reveals actual process flows and identifies bottlenecks or inefficiencies.',
      classification: 'ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Needs detailed event logs and process understanding for meaningful results.'
    },
    {
      name: 'Rule-Based Systems',
      description: 'Traditional systems that use predefined rules for decision making.',
      businessValue: 'Provides consistent, transparent decision-making based on explicit business rules.',
      classification: 'Legacy',
      source: 'Claude Sonnet 3.5',
      implementation: 'Requires clear rule definition and regular updates. Limited to well-defined scenarios.'
    },
    {
      name: 'Statistical Outlier Detection',
      description: 'Traditional statistical methods for identifying unusual data points.',
      businessValue: 'Reliable approach for finding anomalies in numerical data.',
      classification: 'Legacy',
      source: 'GPT-4o',
      implementation: 'Works well for simple cases but may miss complex patterns.'
    },
    {
      name: 'Time-Series Analysis',
      description: 'Methods for analyzing data points collected over time to extract meaningful patterns.',
      businessValue: 'Helps understand trends, seasonality, and patterns in time-based data.',
      classification: 'ML',
      source: 'Claude Sonnet 3.5',
      implementation: 'Requires sufficient historical data and understanding of seasonal patterns.'
    },
    {
      name: 'Time-Series Change Detection',
      description: 'Techniques for identifying significant changes in time-series data patterns.',
      businessValue: 'Quickly identifies shifts in trends or behavior patterns over time.',
      classification: 'ML',
      source: 'Gemini Pro 1.5',
      implementation: 'Needs definition of what constitutes significant change for the specific use case.'
    },
    {
      name: 'User Behavior Analytics',
      description: 'Analysis of patterns in user actions and behaviors within systems.',
      businessValue: 'Identifies security threats, improves user experience, and detects fraudulent behavior.',
      classification: 'ML',
      source: 'GPT-4o',
      implementation: 'Requires collection of user activity data while respecting privacy concerns.'
    }
  ];

  const getFeasibilityColor = (feasibility: string) => {
    switch (feasibility) {
      case 'Fully Feasible':
        return 'text-green-700 bg-green-50';
      case 'Partially Feasible':
        return 'text-yellow-700 bg-yellow-50';
      default:
        return 'text-red-700 bg-red-50';
    }
  };

  const getFeasibilityIcon = (feasibility: string) => {
    switch (feasibility) {
      case 'Fully Feasible':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'Partially Feasible':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">Technology Reference Guide</CardTitle>
        <p className="text-gray-600">A practical guide for business leaders</p>
      </CardHeader>
      <CardContent>
        <div className="mb-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Important Note:</strong> While AI and ML technologies offer significant potential, they are tools requiring thoughtful implementation rather than overnight solutions. Success depends on:
            </p>
            <ul className="text-sm text-blue-700 mt-2 list-disc pl-4">
              <li>Clear business objectives</li>
              <li>Quality data</li>
              <li>Appropriate technology selection</li>
              <li>Proper implementation and maintenance</li>
            </ul>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="use-cases">Billing QA Use Cases</TabsTrigger>  
            <TabsTrigger value="classifications">Classifications</TabsTrigger> 
            <TabsTrigger value="technologies">Technologies</TabsTrigger>     
          </TabsList>
          
          <TabsContent value="use-cases">
            <div className="space-y-4">
              {useCases.map((useCase) => (
                <Card key={useCase.scenario} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold">{useCase.scenario}</h3>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getFeasibilityColor(useCase.feasibility)}`}>
                      {getFeasibilityIcon(useCase.feasibility)}
                      <span className="text-sm font-medium">{useCase.feasibility}</span>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-3">{useCase.comments}</p>
                  <div className="flex flex-wrap gap-2">
                    {useCase.technologies.map((tech) => (
                      <span key={tech} className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {tech}
                      </span>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
                  
         <TabsContent value="classifications">
            <div className="space-y-6">
              {classifications.map((item) => (
                <Card key={item.term} className="p-4">
                  <h3 className="text-lg font-semibold mb-2">{item.term}</h3>
                  <p className="text-gray-600 mb-2">{item.definition}</p>
                  <div className="bg-yellow-50 p-3 rounded-lg mb-2">
                    <p className="text-sm text-yellow-800">
                      <strong>Business Note:</strong> {item.businessNote}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-500 mt-1" />
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Examples: </span>
                      {item.examples}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
          
                  
          <TabsContent value="technologies">
            <div className="space-y-4">
              {technologies.map((tech) => (
                <Card key={tech.name} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold">{tech.name}</h3>
                    <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded">
                      {tech.classification}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{tech.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg mb-2">
                    <p className="text-sm text-green-800">
                      <strong>Business Value:</strong> {tech.businessValue}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg mb-2">
                    <p className="text-sm text-gray-700">
                      <strong>Implementation Note:</strong> {tech.implementation}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500">Source: {tech.source}</p>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TechReferenceGuide;              
